/** @jsx jsx */
import { useFavorites } from '@bottlebooks/gatsby-theme-event/plugins/gatsby-plugin-favorites/src';
import useBaseMenuQuery from '@bottlebooks/gatsby-theme-event/src/components/Layout/useMenuQuery';
import { graphql, useStaticQuery } from 'gatsby';
import { gql } from 'urql';
import useLink from '../../../../useLink';
import usePreviewData from '../../../../usePreviewData';

// The CMS is almost always active, but pointing to a collection without any data
// Therefore we use the baseMenuQuery to fall back to the event data.
export default function useMenuQuery({ locale }) {
  const cmsLink = useLink();
  const eventMenuItems = useBaseMenuQuery({ locale });
  // Check CMS for site config
  const result = useStaticQuery(query);
  const collectionId = result?.collection?.collectionId;
  const sites = result?.allSanitySite?.nodes;
  const cmsSite = sites?.find((site) => site.collectionId === collectionId);
  const enhance = useMenuEnhancer();

  // We want to use preview data if available
  // At the moment, we are only loading published data, not draft data
  const [previewData] = usePreviewData();
  const site = previewData?.data?.site || cmsSite;
  const noLivePreviewUrl = '/cms-preview/pages/noPreview';
  const mappedMenuItems = site?.mainNavigation.map((menuItem) => {
    switch (menuItem.page.__typename) {
      case 'BottlebooksPage':
        // Landing page
        // There is a standard page type of landing page that users
        // can select. It is necessary because the landing page should
        // not have a slug.
        if (menuItem.page.type === '')
          return {
            name: menuItem.name,
            url: cmsLink.page(site.frontpage),
            type: 'otherUrl',
          };
        else
          return {
            name: menuItem.name,
            url: noLivePreviewUrl,
            type: 'otherUrl',
          };
      case 'SanityBottlebooksPage':
        return {
          name: menuItem.name,
          // url is automatically generated by the directory
          // url: cmsLink.page(menuItem.page),
          type: menuItem.page.type,
        };
      case 'Page':
      case 'SanityPage':
        return {
          name: menuItem.name,
          url: cmsLink.page(menuItem.page),
          type: 'otherUrl',
        };
      case 'ExternalPage':
        if (!menuItem.page.url.startsWith('http'))
          return {
            name: menuItem.name,
            url: noLivePreviewUrl,
            type: 'otherUrl',
          };
        else
          return {
            name: menuItem.name,
            url: cmsLink.page(menuItem.page),
            type: menuItem.page.type,
          };
      case 'SanityExternalPage':
        return {
          name: menuItem.name,
          url: cmsLink.page(menuItem.page),
          type: menuItem.page.type,
        };
      default:
        return menuItem;
    }
  });

  const menuItems = mappedMenuItems?.filter((item) => item.name || item.url);
  return menuItems?.map(enhance) || eventMenuItems;
}

/**
 * Not sure if we need this anymore. Favorites is now a permanent item
 * in the header.
 * Keeping because it is an interesting pattern.
 * @returns
 */
function useMenuEnhancer() {
  const { favorites } = useFavorites();
  return function enhanceMenuItem(item) {
    if (item.type === 'tastingList')
      return { ...item, counter: favorites?.length };
    return item;
  };
}

const query = graphql`
  {
    collection: event {
      collectionId: eventId
    }
    allSanitySite {
      nodes {
        collectionId
        mainNavigation {
          name
          page {
            __typename
            ... on SanityBottlebooksPage {
              type
            }
            ... on SanityPage {
              slug {
                current
              }
            }
            ... on SanityExternalPage {
              title
              type
              url
            }
          }
        }
      }
    }
  }
`;

useMenuQuery.fragment = gql`
  fragment useMenuQueryFragment on Site {
    collectionId
    mainNavigation {
      name
      page {
        __typename
        ... on BottlebooksPage {
          type
          id: _id
        }
        ... on Page {
          id: _id
          slug {
            current
          }
        }
        ... on ExternalPage {
          title
          type
          url
        }
      }
    }
  }
`;
